import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../shared/keys";
import type { Action, ActionCreate, ActionUpdate } from "../shared/types";

export const RECENT_RUNS = 8;

export function useActionsData(agentId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const agent = { params: { path: { agent_id: agentId } } };
  const actions = useQuery({ queryKey: buildKeys.actions(ws, agentId), queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/actions", agent)) });
  const stats = useQuery({ queryKey: buildKeys.actionStats(ws, agentId), queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/actions/stats", agent)) });
  const runs = useQuery({
    queryKey: buildKeys.actionRuns(ws, agentId),
    queryFn: () => unwrap(api.GET("/v1/action-runs", { params: { query: { agent_id: agentId, limit: RECENT_RUNS } } })),
  });
  return { actions, stats, runs };
}

/** `GET /v1/action-runs/export`: every confirmed or finished run of the last 7 days, redacted server-side. */
export function useExportRuns(agentId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: () => unwrap(api.GET("/v1/action-runs/export", { params: { query: { agent_id: agentId } }, parseAs: "blob" })),
  });
}

export function useActionMutations(agentId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: buildKeys.actions(ws, agentId) }),
    client.invalidateQueries({ queryKey: buildKeys.actionStats(ws, agentId) }),
    client.invalidateQueries({ queryKey: buildKeys.actionRuns(ws, agentId) }),
  ]);
  const path = (id: string) => ({ params: { path: { action_id: id } } });
  const create = useMutation({
    mutationFn: (body: ActionCreate) => unwrap(api.POST("/v1/agents/{agent_id}/actions", { params: { path: { agent_id: agentId } }, body })),
    onSuccess: refresh,
  });
  const createBooking = useMutation({
    mutationFn: (name: string) => unwrap(api.POST("/v1/agents/{agent_id}/actions/booking", { params: { path: { agent_id: agentId } }, body: { name } })),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ActionUpdate }) => unwrap(api.PATCH("/v1/actions/{action_id}", { ...path(id), body })),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: (action: Action) => unwrap(api.PATCH("/v1/actions/{action_id}", { ...path(action.id), body: { enabled: !action.enabled } })),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/actions/{action_id}", path(id))),
    onSuccess: refresh,
  });
  const test = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) => unwrap(api.POST("/v1/actions/{action_id}/test", { ...path(id), body: { input } })),
  });
  return { create, createBooking, update, toggle, remove, test };
}
